import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalToastTypeNames, ToastTypes } from 'types';
import {
  useZeroContributionMutation,
  StudentDetailDocument,
} from 'core/graphql';

import { useToasts } from '../Toast/useToasts';

interface IUseZeroContributionHook {
  /** Zero a single unpaid contribution on a transferred-out agreement. */
  zero: (contributionId: string) => Promise<boolean>;
  loading: boolean;
}

/**
 * Wind an old group's schedule down to zero during a transfer. Wraps the
 * `zeroOutContribution` mutation with a toast + StudentDetail refetch so the
 * card repaints. The backend strictly gates it (terminated + transferOut + no
 * linked payment), so a refused row surfaces its customError.
 */
export const useZeroContribution = (
  studentId?: string,
): IUseZeroContributionHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();

  const refetchQueries = useMemo(
    () =>
      studentId
        ? [{ query: StudentDetailDocument, variables: { id: studentId } }]
        : [],
    [studentId],
  );

  const [mutation, state] = useZeroContributionMutation({
    refetchQueries,
  });

  const notifyError = useCallback(
    (text?: string) => {
      addToast({
        data: {
          type: ToastTypes.error,
          title: t('pages.studentDetail.agreement.zeroFailed'),
          text,
        },
        type: GlobalToastTypeNames.FakturowniaMatch,
      });
    },
    [addToast, t],
  );

  const zero = useCallback(
    async (contributionId: string) => {
      try {
        const result = await mutation({ variables: { contributionId } });
        const payload = result.data?.zeroOutContribution;
        if (payload?.success) return true;
        notifyError(
          payload?.customErrors
            ?.map((e) => e?.message || e?.code || null)
            .filter(Boolean)
            .join('\n') || undefined,
        );
        return false;
      } catch (error) {
        notifyError((error as Error).message);
        return false;
      }
    },
    [mutation, notifyError],
  );

  return { zero, loading: state.loading };
};
